import React, { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import Sidebar, { SidebarItem, SidebarItemType } from './sidebar';
import SidebarChip from './sidebar-chip';
import { LogoCreatorIc, LogoPartnerIc } from '../icons';
import { useSidebarCounts } from '../../contexts/SidebarCountsContext';
import { Icon } from '@iconify/react';
import { Tooltip, cn } from '@heroui/react';
import { getButtonVariantColorsByUserRole } from '../../theme/colors';

const createBadge = (value: number | string, ariaLabel?: string) => (
  <SidebarChip ariaLabel={ariaLabel}>{value}</SidebarChip>
);

interface SidebarCounts {
  creatorsCount: number;
  partnersCount: number;
  accountsCount: number;
  cplOffersCount: number;
  cplOrdersCount: number;
  revshareOffersCount: number;
  revshareCampaignsCount: number;
  revshareBillsCount: number;
  withdrawRequestsCount: number;
}

const getSidebarItems = (counts: SidebarCounts): SidebarItem[] => [
  {
    key: 'dashboard',
    href: '/',
    icon: 'solar:widget-2-linear',
    title: 'Дашборд',
  },
  {
    key: 'users',
    title: 'Пользователи',
    type: SidebarItemType.Nest,
    items: [
      {
        key: 'users-creators',
        href: '/users/creators',
        icon: 'solar:checklist-minimalistic-linear',
        title: 'Криэйторы',
        endContent: createBadge(counts.creatorsCount, `Количество криэйторов: ${counts.creatorsCount}`),
      },
      {
        key: 'users-partners',
        href: '/users/partners',
        icon: 'solar:cart-large-minimalistic-linear',
        title: 'Партнёры',
        endContent: createBadge(counts.partnersCount, `Количество партнёров: ${counts.partnersCount}`),
      },
    ],
  },
  {
    key: 'accounts',
    title: 'Аккаунты',
    type: SidebarItemType.Nest,
    items: [
      {
        key: 'accounts-onlyfans',
        href: '/accounts/onlyfans',
        icon: 'solar:cart-large-minimalistic-linear',
        title: 'Аккаунты OnlyFans',
        endContent: createBadge(counts.accountsCount, `Аккаунты OnlyFans: ${counts.accountsCount}`),
      },
    ],
  },
  {
    key: 'cpl',
    title: 'CPL',
    type: SidebarItemType.Nest,
    items: [
      {
        key: 'cpl-offers',
        href: '/cpl/offers',
        icon: 'solar:bill-list-linear',
        title: 'Офферы',
        endContent: createBadge(counts.cplOffersCount, `CPL офферы: ${counts.cplOffersCount}`),
      },
      {
        key: 'cpl-orders',
        href: '/cpl/orders',
        icon: 'solar:bill-list-linear',
        title: 'Заказы',
        endContent: createBadge(counts.cplOrdersCount, `CPL заказы: ${counts.cplOrdersCount}`),
      },
    ],
  },
  {
    key: 'revshare',
    title: 'RevShare',
    type: SidebarItemType.Nest,
    items: [
      {
        key: 'revshare-offers',
        href: '/revshare/offers',
        icon: 'solar:bill-list-linear',
        title: 'Офферы',
        endContent: createBadge(counts.revshareOffersCount, `RevShare офферы: ${counts.revshareOffersCount}`),
      },
      {
        key: 'revshare-campaigns',
        href: '/revshare/campaigns',
        icon: 'solar:bill-list-linear',
        title: 'Кампании',
        endContent: createBadge(counts.revshareCampaignsCount, `RevShare кампании: ${counts.revshareCampaignsCount}`),
      },
      {
        key: 'revshare-bills',
        href: '/revshare/bills',
        icon: 'solar:bill-list-linear',
        title: 'Счета',
        endContent: createBadge(counts.revshareBillsCount, `RevShare счета: ${counts.revshareBillsCount}`),
      },
    ],
  },
  {
    key: 'account',
    title: 'Аккаунт',
    type: SidebarItemType.Nest,
    items: [
      {
        key: 'account-settings',
        href: '/account/settings',
        icon: 'solar:settings-linear',
        title: 'Настройки',
      },
      {
        key: 'account-finance',
        href: '/account/finance',
        icon: 'solar:wallet-linear',
        title: 'Финансы',
      },
      {
        key: 'account-withdraw',
        href: '/account/withdraw',
        icon: 'solar:bill-list-linear',
        title: 'Заявки на выплату',
        endContent: createBadge(counts.withdrawRequestsCount, `Заявки на выплату: ${counts.withdrawRequestsCount}`),
      },
    ],
  },
];

const computeSelectedKey = (pathname: string, items: SidebarItem[]) => {
  let bestMatch: { key: string; length: number } = { key: 'dashboard', length: 0 };

  const checkItem = (item: SidebarItem) => {
    if (item.href) {
      if (pathname === item.href) {
        bestMatch = { key: item.key, length: item.href.length };
        return;
      }
      if (pathname.startsWith(item.href + '/') && item.href.length > bestMatch.length) {
        bestMatch = { key: item.key, length: item.href.length };
      }
    }
    item.items?.forEach(checkItem);
  };

  items.forEach(checkItem);
  return bestMatch.key;
};

const AdminSidebar: React.FC = () => {
  const location = useLocation();
  const { counts } = useSidebarCounts();

  // Initialize from localStorage
  const [isManuallyExpanded, setIsManuallyExpanded] = useState(() => {
    const saved = localStorage.getItem('admin-sidebar-manually-expanded');
    return saved ? JSON.parse(saved) : false;
  });

  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem('admin-sidebar-collapsed');
    if (saved !== null) {
      return JSON.parse(saved);
    }
    return window.innerWidth < 1300;
  });

  const sidebarItems = React.useMemo(() => getSidebarItems(counts), [counts]);
  const primaryColors = React.useMemo(() => getButtonVariantColorsByUserRole('primary', 'admin'), []);

  const selectedKey = React.useMemo(
    () => computeSelectedKey(location.pathname, sidebarItems),
    [location.pathname, sidebarItems],
  );

  // Handle responsive collapse
  useEffect(() => {
    const handleResize = () => {
      if (!isManuallyExpanded) {
        const shouldCollapse = window.innerWidth < 1300;
        setIsCollapsed(shouldCollapse);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isManuallyExpanded]);

  const toggleSidebar = () => {
    const newManuallyExpanded = !isManuallyExpanded;
    const newCollapsed = !isCollapsed;

    setIsManuallyExpanded(newManuallyExpanded);
    setIsCollapsed(newCollapsed);

    // Save to localStorage
    localStorage.setItem('admin-sidebar-manually-expanded', JSON.stringify(newManuallyExpanded));
    localStorage.setItem('admin-sidebar-collapsed', JSON.stringify(newCollapsed));
  };

  const renderCollapsedItem = (item: SidebarItem, showDivider = false) => {
    if (!item.href) return null;
    const isSelected = selectedKey === item.key;

    return (
      <div key={item.key} className={cn("flex justify-center", showDivider && "pb-4 mb-4 border-b border-[#E5E5E5]")}>
        <Tooltip
          content={item.title}
          placement="right"
          classNames={{
            content: "bg-[#18181B] text-white text-[12px] px-2 py-1"
          }}
        >
          <Link
            to={item.href}
            className={cn(
              "flex items-center justify-center w-[44px] h-[44px] rounded-lg",
              "transition-colors duration-150 ease-in-out relative group",
              isSelected
                ? null
                : "hover:bg-default-300/20"
            )}
            style={isSelected ? { backgroundColor: primaryColors.background } : undefined}
          >
            {item.icon && (
              <Icon
                icon={item.icon}
                width={24}
                className={cn(
                  "transition-colors duration-150",
                  isSelected ? "text-white" : "text-[#71717A]"
                )}
              />
            )}
            {item.endContent && (
              <div className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] flex items-center justify-center rounded-full bg-[#006FEE] text-white text-[9px] font-medium px-[3px]">
                {typeof item.endContent === 'object' && 'props' in item.endContent ? (item.endContent as any).props.children : ''}
              </div>
            )}
          </Link>
        </Tooltip>
      </div>
    );
  };

  const renderCollapsedSection = (item: SidebarItem) => {
    return (
      <div key={item.key} className="flex flex-col items-center space-y-2 pb-4 mb-4 border-b border-[#E5E5E5] last:border-0 last:pb-0 last:mb-0">
        {item.items?.map((subItem) => renderCollapsedItem(subItem, false))}
      </div>
    );
  };

  return (
    <div className="h-screen">
      <div
        className={cn(
          "relative flex h-full flex-col border-r-small border-divider bg-white py-[24px] transition-all duration-300",
          isCollapsed ? "w-[90px] px-[14px]" : "w-[291px] px-[16px]"
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between flex-shrink-0 mb-6">
          {!isCollapsed ? (
            <div className="flex items-center gap-[8px] px-2">
              <LogoPartnerIc className='w-[75px]' />
              <span className="text-[12px] font-regular text-[#71717A] select-none">Администратор</span>
            </div>
          ) : (
            <Tooltip
              content="OnlyU Администратор"
              placement="right"
              classNames={{
                content: "bg-[#18181B] text-white text-[12px] px-2 py-1"
              }}
            >
              <div className="flex items-center justify-center w-full cursor-pointer">
                <LogoPartnerIc className='w-[40px]' />
              </div>
            </Tooltip>
          )}
        </div>

        {/* Toggle Button */}
        <div className="mb-4 flex justify-center">
          <Tooltip
            content={isCollapsed ? "Развернуть меню" : "Свернуть меню"}
            placement="right"
            classNames={{
              content: "bg-[#18181B] text-white text-[12px] px-2 py-1"
            }}
          >
            <button
              onClick={toggleSidebar}
              className={cn(
                "flex items-center justify-center gap-2 rounded-lg transition-colors",
                "bg-[#F4F4F5] hover:bg-[#E4E4E7]",
                isCollapsed ? "w-[32px] h-[32px]" : "px-3 py-2"
              )}
            >
              {!isCollapsed && (
                <span className="text-[12px] font-medium text-[#71717A] select-none">
                  Свернуть меню
                </span>
              )}
              <Icon
                icon={isCollapsed ? "solar:alt-arrow-right-linear" : "solar:alt-arrow-left-linear"}
                width={20}
                className="text-[#71717A] flex-shrink-0"
              />
            </button>
          </Tooltip>
        </div>

        {/* Navigation */}
        <div className={cn(
          "flex-1 overflow-y-auto py-6",
          isCollapsed ? "" : "-mr-6 pr-6"
        )}>
          {isCollapsed ? (
            <div>
              {sidebarItems.map((item, index) => {
                if (item.type === SidebarItemType.Nest && item.items) {
                  return renderCollapsedSection(item);
                } else if (item.href) {
                  // Add divider after first standalone item (Dashboard)
                  return renderCollapsedItem(item, index === 0);
                }
                return null;
              })}
            </div>
          ) : (
            <Sidebar
              items={sidebarItems}
              selectedKey={selectedKey}
              userRole="admin"
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminSidebar;
